import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';

import { Animation, TransitionChildren } from '@unfold/animation';
import { useKeyPress } from '@unfold/utils';

import { IModalProps } from '../common/types';

import { Dialog } from './dialog';
import { ModalContainer, Overlay } from './styled';

export const Modal: React.FC<IModalProps> = ({
  title,
  isOpen,
  hasCloseButton = true,
  onCloseComplete,
  width,
  shouldCloseOnEscapePress = true,
  shouldCloseOnOverlayClick = true,
  headerActions,
  footerActions,
  customFooter,
  onClose,
  children,
  size = 'XLARGE',
  className,
  titleIcon,
  hasPadding = true,
}) => {
  const isEscPressed = useKeyPress('Esc', 27);

  const handleOverlayClick = (): void => {
    if (shouldCloseOnOverlayClick) {
      onClose();
    }
  };

  useEffect(() => {
    if (shouldCloseOnEscapePress && isEscPressed) {
      onClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEscPressed, shouldCloseOnEscapePress]);

  return (
    <Animation
      in={isOpen}
      unmountOnExit
      onExited={onCloseComplete}
      transitions={['slideUp', 'fade']}
    >
      {({ fade, slideUp }): TransitionChildren => {
        return createPortal(
          <ModalContainer css={fade} className={className}>
            <Overlay role="presentation" onClick={handleOverlayClick} />
            <Dialog
              role="dialog"
              aria-modal
              css={slideUp}
              title={title}
              headerActions={headerActions}
              footerActions={footerActions}
              customFooter={customFooter}
              onClose={onClose}
              width={width}
              size={size}
              hasCloseButton={hasCloseButton}
              titleIcon={titleIcon}
              hasPadding={hasPadding}
            >
              {children}
            </Dialog>
          </ModalContainer>,
          document.body
        );
      }}
    </Animation>
  );
};
